import React, { useEffect, useRef, useState } from "react";
import "./HomeSelector.css";
import { ListGroup } from "react-bootstrap";
import Badge from "react-bootstrap/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import HomeNameBadge from "../../Partials/HomeNameBadge/index.jsx";
import TimestampBadge from "../../Partials/TimestampBadge";
import { useTimezone } from "../../../contexts/TimezoneContext";
import { useAuth } from "../../../../../contexts/AuthContext";
import { getHome } from "../../../libs/dashboard";
import { getBondColor } from "../../../../../lib/ui/colorUtils.js";

function roleBadgeVariant(role) {
    if (role === 'owner') return 'success';
    if (role === 'dealer') return 'warning';
    return 'secondary';
}

// Approximate two-line row height used to size the expanded scroll viewport
const ROW_HEIGHT_PX = 72;
const EXPANDED_ROWS = 10;

function HomeRowContent({ home, bondIds }) {
    const { formatDateCustom } = useTimezone();
    return (
        <>
            <div className="d-flex justify-content-between align-items-center w-100">
                <div className="d-flex flex-column align-items-start" style={{ gap: '0.15rem' }}>
                    <div className="d-flex align-items-center" style={{ gap: '0.25rem' }}>
                        <HomeNameBadge name={home.name} />
                        <Badge bg={roleBadgeVariant(home.role)}>{home.role}</Badge>
                    </div>
                    <small className="text-muted">{home.home_code}</small>
                </div>
                <div className="d-flex align-items-center" style={{ gap: '0.25rem' }}>
                    <Badge bg="dark">{home.bond_count} bonds</Badge>
                    <Badge bg="secondary">{home.member_count} members</Badge>
                    {home.created && (
                        <TimestampBadge
                            value={home.created}
                            absolute={formatDateCustom(home.created, 'date')}
                            label="Created"
                            bg="light"
                            text="dark"
                        />
                    )}
                </div>
            </div>
            {bondIds.length > 0 && (
                <div className="d-flex flex-wrap" style={{ gap: '0.25rem' }}>
                    {bondIds.map((bondId) => (
                        <Badge key={bondId} className="bondBadge" style={{ '--bond-badge-bg': getBondColor(bondId), color: '#ffffff', fontSize: '0.7rem' }}>{bondId}</Badge>
                    ))}
                </div>
            )}
        </>
    );
}

/**
 * Home selector dropdown above the event stream. Collapsed, it shows the
 * currently selected home as a single row with a large expand arrow; open, it
 * reveals the full selectable homes list (mirroring the left HomesList rows,
 * plus each home's bond-id pills fetched lazily from the home-detail endpoint).
 */
function HomeSelector({ homes = [], selectedHomeId = null, onSelectHome }) {
    const { getToken } = useAuth();
    const [bondsByHome, setBondsByHome] = useState({});
    const [open, setOpen] = useState(false);
    const fetchedRef = useRef(new Set());

    useEffect(() => {
        let cancelled = false;
        const toFetch = homes.filter(h => h.bond_count > 0 && !fetchedRef.current.has(h.id));
        if (toFetch.length === 0) return;
        toFetch.forEach(h => fetchedRef.current.add(h.id));

        (async () => {
            for (const home of toFetch) {
                try {
                    const detail = await getHome(getToken(), home.id);
                    if (cancelled) return;
                    setBondsByHome(prev => ({ ...prev, [home.id]: (detail?.bonds || []).map(b => b.bond_id) }));
                } catch (err) {
                    console.error('Error fetching bonds for home', home.id, err);
                    if (!cancelled) fetchedRef.current.delete(home.id); // allow retry on next render cycle
                }
            }
        })();

        return () => { cancelled = true; };
    }, [homes, getToken]);

    if (!homes || homes.length === 0) return null;

    const selectedHome = homes.find(h => h.id === selectedHomeId) || null;

    const handleSelect = (homeId) => {
        onSelectHome(homeId);
        setOpen(false);
    };

    return (
        <div className="w-100 mb-2 homeSelector">
            {/* Collapsed header: selected home + big expand arrow */}
            <div
                className="homeSelectorToggle d-flex align-items-center"
                onClick={() => setOpen(o => !o)}
                role="button"
                title={open ? 'Collapse homes list' : 'Expand homes list'}
            >
                <div className="homeSelectorArrow d-flex align-items-center justify-content-center">
                    <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} size="lg" />
                </div>
                <div className="flex-grow-1 d-flex flex-column" style={{ gap: '0.25rem', minWidth: 0 }}>
                    {selectedHome ? (
                        <HomeRowContent home={selectedHome} bondIds={bondsByHome[selectedHome.id] || []} />
                    ) : (
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <span className="text-muted">Select a home…</span>
                            <Badge bg="dark">{homes.length} homes</Badge>
                        </div>
                    )}
                </div>
            </div>

            {/* Expanded: full homes list, selectable */}
            {open && (
                <div className="homeSelectorScroll mt-1" style={{ maxHeight: `${EXPANDED_ROWS * ROW_HEIGHT_PX}px`, overflowY: 'auto' }}>
                    <ListGroup>
                        {homes.map((home) => (
                            <ListGroup.Item
                                key={home.id}
                                action
                                active={home.id === selectedHomeId}
                                onClick={() => handleSelect(home.id)}
                                className="d-flex flex-column"
                                style={{ gap: '0.25rem' }}
                            >
                                <HomeRowContent home={home} bondIds={bondsByHome[home.id] || []} />
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </div>
            )}
        </div>
    );
}

export default HomeSelector;
